import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Users,
  Handshake,
  ArrowRight,
  CheckCircle,
  Heart,
  Briefcase,
  Send,
} from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const benefits = [
  "Competitive salary and benefits package",
  "Comprehensive training and development",
  "Supportive team environment",
  "Career progression opportunities",
  "Making a real difference to young lives",
  "Flexible working arrangements",
];

const partnerTypes = [
  "Local Authority",
  "Housing Association",
  "Training Provider",
  "Charity / Third Sector",
  "Corporate Partner",
  "Community Organisation",
  "Other",
];

export default function WorkWithUsPage() {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [activeTab, setActiveTab] = useState("join");
  
  const [joinFormData, setJoinFormData] = useState({
    name: "",
    email: "",
    phone: "",
    current_role: "",
    experience: "",
    message: "",
  });

  const [partnerFormData, setPartnerFormData] = useState({
    name: "",
    email: "",
    phone: "",
    organisation_name: "",
    partnership_type: "",
    message: "",
  });

  const handleJoinInputChange = (e) => {
    const { name, value } = e.target;
    setJoinFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handlePartnerInputChange = (e) => {
    const { name, value } = e.target;
    setPartnerFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleJoinSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await axios.post(`${API}/work-with-us`, {
        ...joinFormData,
        form_type: "join_team",
      });
      
      toast.success("Application submitted successfully! We'll be in touch soon.");
      setJoinFormData({
        name: "",
        email: "",
        phone: "",
        current_role: "",
        experience: "",
        message: "",
      });
    } catch (error) {
      console.error("Error submitting form:", error);
      toast.error("Failed to submit. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handlePartnerSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await axios.post(`${API}/work-with-us`, {
        ...partnerFormData,
        form_type: "partnership",
      });
      
      toast.success("Enquiry submitted successfully! We'll be in touch soon.");
      setPartnerFormData({
        name: "",
        email: "",
        phone: "",
        organisation_name: "",
        partnership_type: "",
        message: "",
      });
    } catch (error) {
      console.error("Error submitting form:", error);
      toast.error("Failed to submit. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="overflow-hidden">
      {/* Hero Section */}
      <section className="relative py-20 md:py-32 bg-gradient-to-br from-slate-50 via-white to-teal-50/30">
        <div className="blob-teal -top-40 -right-40" />
        <div className="container-custom relative z-10">
          <div className="max-w-3xl">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <div className="inline-flex items-center gap-2 bg-teal-50 text-teal-700 px-4 py-2 rounded-full text-sm font-medium mb-6">
                <Users className="w-4 h-4" />
                Work With Us
              </div>
              <h1
                className="text-4xl sm:text-5xl font-extrabold text-slate-900 leading-tight mb-6"
                data-testid="work-with-us-headline"
              >
                Join us in making a{" "}
                <span className="text-teal-600">difference</span>
              </h1>
              <p className="text-lg text-slate-600 leading-relaxed">
                Whether you're looking to join our team or explore partnership
                opportunities, we'd love to hear from you.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Tabs Section */}
      <section className="section-padding bg-white" data-testid="work-with-us-forms">
        <div className="container-custom">
          <Tabs value={activeTab} onValueChange={setActiveTab} className="max-w-4xl mx-auto">
            <TabsList className="grid w-full grid-cols-2 mb-12 p-1 bg-slate-100 rounded-full">
              <TabsTrigger
                value="join"
                className="rounded-full data-[state=active]:bg-white data-[state=active]:shadow-sm py-3"
                data-testid="tab-join-team"
              >
                <Users className="w-4 h-4 mr-2" />
                Join the Team
              </TabsTrigger>
              <TabsTrigger
                value="partner"
                className="rounded-full data-[state=active]:bg-white data-[state=active]:shadow-sm py-3"
                data-testid="tab-partnerships"
              >
                <Handshake className="w-4 h-4 mr-2" />
                Partnerships
              </TabsTrigger>
            </TabsList>

            {/* Join Team Tab */}
            <TabsContent value="join">
              <div className="grid lg:grid-cols-2 gap-12">
                <div>
                  <h2 className="text-2xl font-bold text-slate-900 mb-4">
                    Join Our Team
                  </h2>
                  <p className="text-slate-600 mb-8">
                    We're always looking for passionate individuals who share our
                    commitment to supporting vulnerable young people. If you're
                    interested in working with Epic Care, please complete the form
                    below.
                  </p>

                  <h3 className="font-semibold text-slate-900 mb-4">
                    Why Work With Us
                  </h3>
                  <div className="space-y-3">
                    {benefits.map((benefit) => (
                      <div key={benefit} className="flex items-center gap-3">
                        <CheckCircle className="w-5 h-5 text-teal-500 flex-shrink-0" />
                        <span className="text-slate-700">{benefit}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <form onSubmit={handleJoinSubmit} className="space-y-6">
                  <div className="bg-slate-50 rounded-2xl p-6 md:p-8 space-y-6">
                    <div>
                      <Label htmlFor="join-name">Your Name *</Label>
                      <Input
                        id="join-name"
                        name="name"
                        value={joinFormData.name}
                        onChange={handleJoinInputChange}
                        required
                        className="mt-2"
                        data-testid="join-input-name"
                      />
                    </div>
                    <div>
                      <Label htmlFor="join-email">Email *</Label>
                      <Input
                        id="join-email"
                        name="email"
                        type="email"
                        value={joinFormData.email}
                        onChange={handleJoinInputChange}
                        required
                        className="mt-2"
                        data-testid="join-input-email"
                      />
                    </div>
                    <div>
                      <Label htmlFor="join-phone">Phone</Label>
                      <Input
                        id="join-phone"
                        name="phone"
                        type="tel"
                        value={joinFormData.phone}
                        onChange={handleJoinInputChange}
                        className="mt-2"
                        data-testid="join-input-phone"
                      />
                    </div>
                    <div>
                      <Label htmlFor="join-current_role">Current Role</Label>
                      <Input
                        id="join-current_role"
                        name="current_role"
                        value={joinFormData.current_role}
                        onChange={handleJoinInputChange}
                        className="mt-2"
                        data-testid="join-input-role"
                      />
                    </div>
                    <div>
                      <Label htmlFor="join-experience">Relevant Experience</Label>
                      <Textarea
                        id="join-experience"
                        name="experience"
                        value={joinFormData.experience}
                        onChange={handleJoinInputChange}
                        rows={3}
                        className="mt-2"
                        placeholder="Tell us about your experience working with young people..."
                        data-testid="join-textarea-experience"
                      />
                    </div>
                    <div>
                      <Label htmlFor="join-message">Why Epic Care? *</Label>
                      <Textarea
                        id="join-message"
                        name="message"
                        value={joinFormData.message}
                        onChange={handleJoinInputChange}
                        required
                        rows={4}
                        className="mt-2"
                        placeholder="Tell us why you want to work with Epic Care..."
                        data-testid="join-textarea-message"
                      />
                    </div>
                  </div>

                  <Button
                    type="submit"
                    size="lg"
                    disabled={isSubmitting}
                    className="w-full bg-slate-900 hover:bg-slate-800 text-white rounded-full h-14"
                    data-testid="join-submit-btn"
                  >
                    {isSubmitting ? "Submitting..." : "Submit Application"}
                    <Send className="w-5 h-5 ml-2" />
                  </Button>
                </form>
              </div>
            </TabsContent>

            {/* Partnerships Tab */}
            <TabsContent value="partner">
              <div className="grid lg:grid-cols-2 gap-12">
                <div>
                  <h2 className="text-2xl font-bold text-slate-900 mb-4">
                    Partnership Opportunities
                  </h2>
                  <p className="text-slate-600 mb-8">
                    We believe in the power of collaboration. Whether you're a
                    local authority, training provider, corporate partner or
                    community organisation, we'd love to explore how we can work
                    together.
                  </p>

                  <div className="bg-teal-50 rounded-2xl p-6 mb-8">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 bg-teal-100 rounded-xl flex items-center justify-center flex-shrink-0">
                        <Heart className="w-6 h-6 text-teal-600" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-teal-900 mb-2">
                          Our Partnership Approach
                        </h3>
                        <p className="text-teal-700 text-sm">
                          We work collaboratively with partners who share our
                          commitment to supporting young people and creating
                          positive outcomes. Together, we can make a bigger
                          difference.
                        </p>
                      </div>
                    </div>
                  </div>

                  <h3 className="font-semibold text-slate-900 mb-4">
                    Partnership Types
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {partnerTypes.map((type) => (
                      <span
                        key={type}
                        className="bg-slate-100 text-slate-700 px-4 py-2 rounded-full text-sm"
                      >
                        {type}
                      </span>
                    ))}
                  </div>
                </div>

                <form onSubmit={handlePartnerSubmit} className="space-y-6">
                  <div className="bg-slate-50 rounded-2xl p-6 md:p-8 space-y-6">
                    <div>
                      <Label htmlFor="partner-name">Your Name *</Label>
                      <Input
                        id="partner-name"
                        name="name"
                        value={partnerFormData.name}
                        onChange={handlePartnerInputChange}
                        required
                        className="mt-2"
                        data-testid="partner-input-name"
                      />
                    </div>
                    <div>
                      <Label htmlFor="partner-email">Email *</Label>
                      <Input
                        id="partner-email"
                        name="email"
                        type="email"
                        value={partnerFormData.email}
                        onChange={handlePartnerInputChange}
                        required
                        className="mt-2"
                        data-testid="partner-input-email"
                      />
                    </div>
                    <div>
                      <Label htmlFor="partner-phone">Phone</Label>
                      <Input
                        id="partner-phone"
                        name="phone"
                        type="tel"
                        value={partnerFormData.phone}
                        onChange={handlePartnerInputChange}
                        className="mt-2"
                        data-testid="partner-input-phone"
                      />
                    </div>
                    <div>
                      <Label htmlFor="partner-organisation">Organisation Name *</Label>
                      <Input
                        id="partner-organisation"
                        name="organisation_name"
                        value={partnerFormData.organisation_name}
                        onChange={handlePartnerInputChange}
                        required
                        className="mt-2"
                        data-testid="partner-input-organisation"
                      />
                    </div>
                    <div>
                      <Label htmlFor="partner-type">Partnership Type</Label>
                      <Input
                        id="partner-type"
                        name="partnership_type"
                        value={partnerFormData.partnership_type}
                        onChange={handlePartnerInputChange}
                        className="mt-2"
                        placeholder="e.g., Local Authority, Training Provider"
                        data-testid="partner-input-type"
                      />
                    </div>
                    <div>
                      <Label htmlFor="partner-message">Partnership Enquiry *</Label>
                      <Textarea
                        id="partner-message"
                        name="message"
                        value={partnerFormData.message}
                        onChange={handlePartnerInputChange}
                        required
                        rows={4}
                        className="mt-2"
                        placeholder="Tell us about your organisation and how you'd like to partner with Epic Care..."
                        data-testid="partner-textarea-message"
                      />
                    </div>
                  </div>

                  <Button
                    type="submit"
                    size="lg"
                    disabled={isSubmitting}
                    className="w-full bg-slate-900 hover:bg-slate-800 text-white rounded-full h-14"
                    data-testid="partner-submit-btn"
                  >
                    {isSubmitting ? "Submitting..." : "Submit Enquiry"}
                    <Send className="w-5 h-5 ml-2" />
                  </Button>
                </form>
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </section>
    </div>
  );
}
